import styles from "./RightPanel.module.css";
import classNames from "classnames";
import { ComponentProps, FunctionComponent, useEffect, useState } from "react";
import Icon from "../../components/Icon";
import { TRootState } from "../../store/create-store";
import { useSelector } from "react-redux";
import PointDetails from "./PointDetails";
import PointStyle from "./Styling";
import Stats from "./Functions";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Meta from "./Meta";
import withErrorBoundary from "../../hocs/withErrorBoundary";
import Shortcuts from "./Shortcuts";
import { selectRightPanelState, selectHasDataset } from "../../selectors";
import Debug from "./Debug";
import About from "./About";
import { isProduction } from "../../utils/env.utils";

const TABS: Array<{
  name: string;
  label: string;
  icon: ComponentProps<typeof Icon>["name"];
}> = [
  {
    name: "Details",
    label: "Data Point",
    icon: "ads_click" as const,
  },
  {
    name: "Meta",
    label: "Metadata",
    icon: "info" as const,
  },
  {
    name: "Stats",
    label: "Transforms",
    icon: "functions" as const,
  },
  {
    name: "Style",
    label: "Styling",
    icon: "color_lens" as const,
  },
  {
    name: "Shortcuts",
    label: "Shortcuts",
    icon: "keyboard_command_key" as const,
  },
  {
    name: "About",
    label: "About",
    icon: "help" as const,
  },
];

if (!isProduction) {
  TABS.push({
    name: "Debug",
    label: "Debug",
    icon: "adb" as const,
  });
}

const RightPanel: FunctionComponent = () => {
  const { visible, clickedPoint, hasDataset } = useSelector(selectRightPanel);
  const [currentTab, setCurrentTab] = useState<string>(TABS[0].name);

  useEffect(() => {
    setCurrentTab(TABS[0].name);
  }, [clickedPoint]);

  let content;
  switch (currentTab) {
    case "Details":
      content = <PointDetails clickedPoint={clickedPoint} />;
      break;

    case "Style":
      content = <PointStyle />;
      break;

    case "Stats":
      content = <Stats />;
      break;

    case "Meta":
      content = <Meta />;
      break;

    case "About":
      content = <About />;
      break;

    case "Shortcuts":
      content = <Shortcuts />;
      break;

    case "Debug":
      content = <Debug />;
      break;

    default:
      content = <div>Tab {currentTab} not implemented</div>;
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <section
        className={classNames(styles.wrapper, {
          [styles.show]: visible && hasDataset,
        })}
      >
        <ul className={styles.tabs}>
          {TABS.map((tab) => (
            <li key={tab.name}>
              <button
                onClick={() => setCurrentTab(tab.name)}
                className={classNames(styles.tabButton, {
                  [styles.tabActive]: currentTab === tab.name,
                })}
              >
                <p data-tip={tab.label}>
                  <Icon name={tab.icon} color="dark" />
                </p>
              </button>
            </li>
          ))}
        </ul>

        <div className={styles.content}>{content}</div>
      </section>
    </DndProvider>
  );
};

const selectRightPanel = (state: TRootState) => {
  const hasDataset = selectHasDataset(state);
  const { visible, clickedPoint } = selectRightPanelState(state);

  return {
    hasDataset,
    visible,
    clickedPoint,
  };
};

export default withErrorBoundary(RightPanel);
